import React, { FC, useEffect } from 'react';
import { styled } from 'styled-components';
import { Button } from '../buttons';
import { ValidationBox, ValidationBoxProps } from '../validation/validationBox';
import { ValidationMessageStyled } from '../validation/validationMessage';

export const PageSubtitle = styled.div`
    text-align: center;
    font-size: 16px;
    line-height: 26px;
    margin-top: 0;
    margin-bottom: 30px;
`;

export const PageFooterButtonsInnerContainer = styled.div<{ $fixed?: boolean }>`
    position: relative;
    width: 100%;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    /* Every footer action button (Back, Apply, Go to variants, …) shares the same slim height,
       regardless of its individual size, so the footer row is visually uniform. */
    button {
        box-sizing: border-box !important;
        height: 34px !important;
        min-height: 34px !important;
        align-items: center !important;
    }

    ${(props) =>
        props.$fixed &&
        `
        padding: 10px 48px;
    `}
`;

export const SectionTitle = styled.h5`
    font-size: 16px;
    font-weight: 600;
    color: #263238;
`;

export const SectionSubTitle = styled.h5`
    font-size: 14px;
    font-weight: 600;
    color: #263238;
    margin-top: 30px;
    margin-bottom: 10px;
`;

export const H1 = styled.h1`
    color: ${(props) => props.theme.app.component.pageTitle.textColor};
    margin: 10px 0 20px 0;
    font-size: 18px;
`;

export const H2 = styled.h2`
    color: ${(props) => props.theme.app.component.pageTitle.textColor};
    margin: 10px 0 20px 0;
    font-size: 16px;
`;
export const H3 = styled.h3`
    color: ${(props) => props.theme.app.component.pageTitle.textColor};
    margin: 10px 0 20px 0;
    font-size: 16px;
`;

export const H4 = styled.h4`
    color: ${(props) => props.theme.app.component.pageTitle.textColor};
    margin: 10px 0 20px 0;
    font-size: 14px;
`;

export const H5 = styled.h5`
    color: ${(props) => props.theme.app.component.pageTitle.textColor};
    margin: 10px 0 20px 0;
    font-size: 12px;
`;
export const H6 = styled.h6`
    color: ${(props) => props.theme.app.component.pageTitle.textColor};
    margin: 10px 0 20px 0;
    font-size: 10px;
`;

export const P = styled.div`
    margin-bottom: 20px;
    line-height: 1.6;
`;

export const Caption = styled.span<{ $isError?: boolean }>`
    display: block;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: ${(props) => (props.$isError ? '#DE1C22' : 'rgb(68, 90, 100)')};
    white-space: pre-wrap; /* Preserves line breaks (\n) and multiple spaces. */
`;

export const HeadButtonContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const TitleAndButton = styled.div`
    position: sticky;
    top: ${(props) => (props.theme.additionalProperties?.isToolbarHidden ? `${101 - 55}px` : '101px')};
    z-index: 19;
    background-color: #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 62px;

    ${HeadButtonContainer} {
        margin: 10px 10px 10px 0;
    }
`;
const PageFooterButtonsInnerLeftContainer = styled.div`
    width: 50%;
`;

const PageFooterButtonsInnerRightContainer = styled.div`
    width: 50%;
    display: flex;
    justify-content: flex-end;
    gap: 20px;

    ${Button} {
        min-width: 150px;
    }
`;

const PageFooterValidationBoxContainer = styled(ValidationBox)`
    flex: 1 1 auto;
    min-width: 0;
    align-items: flex-end;

    ${ValidationMessageStyled} {
        display: block;
        max-width: 100%;
        text-align: right;
    }
`;

export const PageFooterValidationBox = <TValue,>(props: ValidationBoxProps<TValue>) => {
    return <PageFooterValidationBoxContainer {...(props as ValidationBoxProps<unknown>)} />;
};

// The fixed footer renders ~54px tall (10px padding × 2 + 34px button row); reserve a bit more so the
// last page element (e.g. the credits banner on the AI tool pages) clears it instead of being clipped.
const FIXED_PAGE_FOOTER_BUTTONS_HEIGHT = 64;
let fixedPageFooterButtonsCount = 0;

function updatePageFooterButtonsHeight(): void {
    if (typeof document === 'undefined') return;

    document.documentElement.style.setProperty(
        '--page-footer-buttons-height',
        fixedPageFooterButtonsCount > 0 ? `${FIXED_PAGE_FOOTER_BUTTONS_HEIGHT}px` : '20px'
    );
}

export const PageFooterButtonsOuterContainer = styled.div<{ $fixed?: boolean; $hasSidebar?: boolean; $fullWidth?: boolean }>`
    margin-top: 40px;
    left: 50px;
    right: 50px;
    background-color: rgba(246, 248, 249, 80%);
    bottom: 0px;
    ${(props) => props.$hasSidebar && `margin-left: calc(var(--app-sidebar-width, 295px) + 10px);`}

    ${(props) =>
        props.$fixed &&
        `
        position: fixed;
        left: ${props.theme.additionalProperties?.isSidebarHidden ? '0' : 'var(--app-sidebar-width, 295px)'};
        right: 0;
        z-index: 1;
    `}

    ${(props) =>
        props.$fullWidth &&
        props.$fixed &&
        `
        left: 0;
        right: 0;
        margin-left: 0;
    `}
`;

interface PageFooterButtonsProps {
    $fixed?: boolean;
    hasSidebar?: boolean;
    fullWidth?: boolean;
    leftContainerContent?: React.ReactNode;
    children?: React.ReactNode;
}

export const PageFooterButtons: FC<PageFooterButtonsProps> = ({ $fixed: fixed = true, hasSidebar, fullWidth, leftContainerContent, children }) => {
    useEffect(() => {
        if (!fixed) return;

        fixedPageFooterButtonsCount += 1;
        updatePageFooterButtonsHeight();

        return () => {
            fixedPageFooterButtonsCount = Math.max(0, fixedPageFooterButtonsCount - 1);
            updatePageFooterButtonsHeight();
        };
    }, [fixed]);

    return (
        <PageFooterButtonsOuterContainer $fixed={fixed} $hasSidebar={hasSidebar} $fullWidth={fullWidth}>
            <PageFooterButtonsInnerContainer $fixed={fixed}>
                <PageFooterButtonsInnerLeftContainer>{leftContainerContent}</PageFooterButtonsInnerLeftContainer>
                <PageFooterButtonsInnerRightContainer>{children}</PageFooterButtonsInnerRightContainer>
            </PageFooterButtonsInnerContainer>
        </PageFooterButtonsOuterContainer>
    );
};
